import { useState, useRef, useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import { env } from '../config/env.js';

const TARGET_SAMPLE_RATE = 16000;
const debugLog = (...args) => { if (env.IS_DEV) console.log(...args); };

export const useAudioRecording = (wsUrl, onTranscriptMessage) => {
  const [micGranted, setMicGranted] = useState(false);
  const [permission, setPermission] = useState('inconnue');
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [status, setStatus] = useState('En attente');
  const [isListening, setIsListening] = useState(false);
  const [hasSpoken, setHasSpoken] = useState(false);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);

  // MediaRecorder for saving full audio recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordedBlobRef = useRef(null);
  const audioBlobReadyCallbackRef = useRef(null); // Callback for when blob is ready

  // Voice Activity Detection - more permissive settings
  const vadStateRef = useRef({
    energyThreshold: 0.005, // Lower threshold for more sensitivity
    silenceFrames: 0,
    maxSilenceFrames: 50, // More frames before considering inactive
    isActive: false,
    lastActiveTime: 0,
    frameCount: 0
  });

  const { connect, disconnect, sendMessage, sendBinaryData, isConnected } = useWebSocket(wsUrl);

  // Voice Activity Detection function - more permissive approach
  const detectVoiceActivity = useCallback((audioData) => {
    const vadState = vadStateRef.current;
    vadState.frameCount++;
    
    // Calculate RMS energy
    let sum = 0;
    for (let i = 0; i < audioData.length; i++) {
      sum += audioData[i] * audioData[i];
    }
    const rms = Math.sqrt(sum / audioData.length);
    
    const currentTime = Date.now();
    
    // For now, let's be very permissive to avoid transcription quality issues
    // Only filter out very quiet frames (likely just noise)
    const isVeryQuiet = rms < 0.001;
    
    if (!isVeryQuiet) {
      // Consider most audio as potentially valid
      vadState.silenceFrames = 0;
      vadState.isActive = true;
      vadState.lastActiveTime = currentTime;
      
      // Debug logging every 100 frames to avoid spam
      if (vadState.frameCount % 100 === 0) {
        console.log(`🎤 VAD: RMS=${rms.toFixed(4)}, sending audio`);
      }
      return true;
    } else {
      // Only filter very quiet frames
      vadState.silenceFrames++;
      
      if (vadState.frameCount % 100 === 0) {
        console.log(`🔇 VAD: RMS=${rms.toFixed(4)}, filtering quiet audio`);
      }
      
      // Still send audio if we were recently active (less aggressive filtering)
      const timeSinceActive = currentTime - vadState.lastActiveTime;
      return timeSinceActive < 2000; // 2 second buffer
    }
  }, []);

  // Waveform visualization
  const drawWaveform = useCallback((canvas, analyser) => {
    if (!canvas || !analyser) return;
    
    const ctx = canvas.getContext('2d');
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    let localAnimationFrame = null;
    
    const draw = () => {
      if (!isRecording) return;
      
      analyser.getByteFrequencyData(dataArray);
      
      // Clear canvas with medical-appropriate background
      ctx.fillStyle = '#f1f5f9';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Medical-appropriate visualization with wider bars and gaps
      const totalBars = Math.min(bufferLength, 24); // Limit number of bars for cleaner look
      const barWidth = 4; // Fixed wider bar width for medical aesthetics
      const barGap = 3; // Larger gap between bars
      const availableWidth = canvas.width - (totalBars * barGap);
      const startX = (canvas.width - (totalBars * barWidth + (totalBars - 1) * barGap)) / 2;
      
      for (let i = 0; i < totalBars; i++) {
        const dataIndex = Math.floor((i / totalBars) * bufferLength);
        const intensity = dataArray[dataIndex] / 255;
        
        // Medical-grade blue with subtle intensity variation
        const alpha = 0.4 + intensity * 0.5; // More subtle intensity range
        ctx.fillStyle = `rgba(59, 130, 246, ${alpha})`; // Medical blue
        
        // Calculate bar height with medical-appropriate scaling
        const barHeight = Math.max(2, intensity * canvas.height * 0.7); // Minimum height for visibility
        
        const x = startX + i * (barWidth + barGap);
        const y = canvas.height - barHeight;
        
        // Draw rounded rectangle for smoother appearance (with fallback)
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(x, y, barWidth, barHeight, 1);
          ctx.fill();
        } else {
          // Fallback for browsers without roundRect
          ctx.fillRect(x, y, barWidth, barHeight);
        }
      }
      
      localAnimationFrame = requestAnimationFrame(draw);
    };
    
    draw();
    
    // Return cleanup function to cancel the animation frame
    return () => {
      if (localAnimationFrame) {
        cancelAnimationFrame(localAnimationFrame);
        localAnimationFrame = null;
      }
    };
  }, [isRecording]);

  // Audio processing functions
  const resampleTo16k = useCallback((input, inputSR) => {
    if (inputSR === TARGET_SAMPLE_RATE) return input;
    
    const ratio = inputSR / TARGET_SAMPLE_RATE;
    const outputLength = Math.round(input.length / ratio);
    const output = new Float32Array(outputLength);
    
    for (let i = 0; i < outputLength; i++) {
      const srcIndex = i * ratio;
      const srcIndexInt = Math.floor(srcIndex);
      const srcIndexFrac = srcIndex - srcIndexInt;
      
      if (srcIndexInt + 1 < input.length) {
        output[i] = input[srcIndexInt] * (1 - srcIndexFrac) + input[srcIndexInt + 1] * srcIndexFrac;
      } else {
        output[i] = input[srcIndexInt] || 0;
      }
    }
    
    return output;
  }, []);

  const f32ToLE = useCallback((f32) => {
    const buf = new ArrayBuffer(f32.length * 4);
    new Float32Array(buf).set(f32);
    return buf;
  }, []);

  const requestMic = useCallback(async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia is not supported in this browser');
      }

      console.log('Requesting microphone permission...');
      console.log('Browser:', navigator.userAgent);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { 
          channelCount: 1, 
          noiseSuppression: true, 
          echoCancellation: true,
          sampleRate: 16000 // Explicitly request 16kHz if supported
        }
      });
      
      setMicGranted(true);
      setPermission('autorisée');
      
      // Clean up the test stream
      stream.getTracks().forEach(track => track.stop());
      
      console.log('Microphone permission granted');
      console.log('Audio track settings:', stream.getAudioTracks()[0]?.getSettings());
    } catch (err) {
      console.error('Microphone permission denied:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      setMicGranted(false);
      setPermission('refusée');
      
      // Provide more specific error messages
      if (err.name === 'NotAllowedError') {
        alert('Microphone access denied. Please allow microphone access and refresh the page.');
      } else if (err.name === 'NotFoundError') {
        alert('No microphone found. Please connect a microphone and refresh the page.');
      } else if (err.name === 'NotSupportedError') {
        alert('Your browser does not support audio recording. Please try a different browser.');
      }
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      if (!micGranted) {
        await requestMic();
        if (!micGranted) return;
      }

      setStatus("Connecting...");
      console.log('Starting WebSocket connection to:', wsUrl);

      const ws = connect(
        // onMessage
        (message) => {
          console.log('Message received:', message.Type || message.type, message.Text || message.text);
          // Handle different possible message structures
          if (message.Type === "partial" || message.type === "partial") {
            const text = message.Text || message.text || "";
            if (text === "[listening...]") {
              setIsListening(true);
            } else if (text && text.length > 0) {
              setIsListening(false);
              setHasSpoken(true);
              onTranscriptMessage(text, false);
            }
          }
          
          if (message.Type === "final" || message.type === "final") {
            const text = message.Text || message.text || "";
            if (text && text.length > 0) {
              setIsListening(false);
              setHasSpoken(true);
              onTranscriptMessage(text, true);
            }
          }
          
          if (message.Type === "error" || message.type === "error") {
            const errorText = message.Text || message.text || "";
            console.error('STT Error:', errorText);
            
            // Handle rate limiting specifically
            if (errorText.includes("Limite de requêtes") || errorText.includes("429")) {
              alert("⚠️ " + errorText);
            }
          }
        },
        // onOpen
        () => {
          console.log('WebSocket opened');
          sendMessage({ 
            type: "start", 
            sample_rate: TARGET_SAMPLE_RATE, 
            language: "fr" 
          });
          setStatus("Recording");
          setIsRecording(true);
          setIsListening(false);
          setHasSpoken(false);
        },
        // onClose
        (event) => {
          console.log('WebSocket closed:', event.code);
          setStatus("Déconnecté");
          setIsRecording(false);
          setIsPaused(false);
        },
        // onError
        (error) => {
          console.error('WebSocket error:', error);
          setStatus("Erreur");
        }
      );

      if (!ws) {
        throw new Error('Failed to create WebSocket connection');
      }

      // Get audio stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, noiseSuppression: true, echoCancellation: true }
      });
      streamRef.current = stream;

      // Setup audio context with better browser compatibility
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        throw new Error('AudioContext is not supported in this browser');
      }
      
      const audioCtx = new AudioContextClass({ 
        sampleRate: TARGET_SAMPLE_RATE 
      });
      audioCtxRef.current = audioCtx;
      
      // Handle AudioContext state for Chrome/Arc - requires user gesture
      if (audioCtx.state === 'suspended') {
        console.log('AudioContext suspended, attempting to resume...');
        try {
          await audioCtx.resume();
          console.log('AudioContext resumed successfully');
        } catch (e) {
          console.error('Failed to resume AudioContext:', e);
          throw new Error('AudioContext requires user interaction to start. Please try clicking the record button again.');
        }
      }
      
      if (audioCtx.state !== 'running') {
        throw new Error(`AudioContext failed to start. State: ${audioCtx.state}`);
      }
      
      console.log('AudioContext state:', audioCtx.state);
      console.log('AudioContext sample rate:', audioCtx.sampleRate);
      
      const inputSR = audioCtx.sampleRate || 48000;
      console.log('AudioContext sample rate:', inputSR);

      const source = audioCtx.createMediaStreamSource(stream);
      
      // Use createScriptProcessor with fallback for better compatibility
      let processor;
      try {
        processor = audioCtx.createScriptProcessor(4096, 1, 1);
        console.log('ScriptProcessor created successfully with 4096 buffer size');
      } catch (e) {
        // Fallback for older browsers
        console.warn('createScriptProcessor failed, trying alternative buffer size:', e);
        try {
          processor = audioCtx.createScriptProcessor(2048, 1, 1);
          console.log('ScriptProcessor created with 2048 buffer size');
        } catch (e2) {
          console.error('All ScriptProcessor attempts failed:', e2);
          throw new Error('Your browser does not support audio processing. Please try a different browser.');
        }
      }
      
      const analyser = audioCtx.createAnalyser();
      
      // Configure analyser for waveform visualization
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      
      processor.onaudioprocess = (e) => {
        if (!ws || ws.readyState !== WebSocket.OPEN || isPaused) return;
        
        const f32in = e.inputBuffer.getChannelData(0);
        
        // Apply voice activity detection to filter noise
        const hasVoiceActivity = detectVoiceActivity(f32in);
        
        if (hasVoiceActivity) {
          // Only send audio data when voice activity is detected
          const f32 = resampleTo16k(f32in, inputSR);
          sendBinaryData(f32ToLE(f32));
        }
      };

      // Connect audio nodes for both processing and visualization
      source.connect(analyser);
      source.connect(processor);
      processor.connect(audioCtx.destination);

      sourceRef.current = source;
      processorRef.current = processor;
      analyserRef.current = analyser;

      // Setup MediaRecorder for saving complete audio
      // Use WebM with Opus codec at 24kbps for smaller file size
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      debugLog('MediaRecorder MIME type:', mimeType);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 24000 // 24 kbps for smaller files
      });

      audioChunksRef.current = []; // Reset chunks array

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          debugLog('Audio chunk captured:', event.data.size, 'bytes');
        }
      };

      mediaRecorder.onstop = () => {
        debugLog('📹 MediaRecorder stopped, total chunks:', audioChunksRef.current.length);
        if (audioChunksRef.current.length === 0) {
          console.warn('⚠️  No audio chunks captured! MediaRecorder may not have recorded anything.');
          recordedBlobRef.current = null;
          // Call callback even if no audio
          if (audioBlobReadyCallbackRef.current) {
            audioBlobReadyCallbackRef.current(null);
            audioBlobReadyCallbackRef.current = null;
          }
          return;
        }
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        recordedBlobRef.current = audioBlob;
        debugLog('✅ Audio recording complete. Size:', audioBlob.size, 'bytes');
        debugLog('✅ Audio blob type:', audioBlob.type);

        // Notify that blob is ready
        if (audioBlobReadyCallbackRef.current) {
          debugLog('🔔 Calling audioBlobReadyCallback with blob size:', audioBlob.size);
          audioBlobReadyCallbackRef.current(audioBlob);
          audioBlobReadyCallbackRef.current = null;
        }
      };

      mediaRecorder.onerror = (error) => {
        console.error('MediaRecorder error:', error);
      };

      // Start MediaRecorder (request data every 10 seconds for better reliability)
      mediaRecorder.start(10000);
      mediaRecorderRef.current = mediaRecorder;
      debugLog('MediaRecorder started');

    } catch (err) {
      console.error('Start recording error:', err);
      setStatus('Erreur: ' + err.message);
    }
  }, [micGranted, requestMic, wsUrl, connect, sendMessage, sendBinaryData, resampleTo16k, f32ToLE, isPaused, onTranscriptMessage, detectVoiceActivity]);

  const stopRecording = useCallback(() => {
    debugLog('Stopping recording');

    // Return a Promise that resolves when the audio blob is ready
    return new Promise((resolve) => {
      // Stop MediaRecorder to finalize the audio blob
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        debugLog('Stopping MediaRecorder...');

        // Set up callback to be called when onstop fires
        audioBlobReadyCallbackRef.current = (blob) => {
          debugLog('✅ stopRecording: Audio blob ready callback fired');
          resolve(blob);
        };

        mediaRecorderRef.current.stop();
      } else {
        // No MediaRecorder or already stopped, resolve immediately
        debugLog('No active MediaRecorder to stop');
        resolve(recordedBlobRef.current);
      }

      // Waveform animation cleanup is now handled by component useEffect

      // Clean up audio
      try {
        streamRef.current?.getTracks().forEach(t => t.stop());
      } catch (e) {
        console.warn('Error stopping stream tracks:', e);
      }

      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }

      streamRef.current = null;
      sourceRef.current = null;
      processorRef.current = null;
      analyserRef.current = null;

      // Reset VAD state
      vadStateRef.current = {
        energyThreshold: 0.005,
        silenceFrames: 0,
        maxSilenceFrames: 50,
        isActive: false,
        lastActiveTime: 0,
        wasLogging: false,
        frameCount: 0
      };

      // Disconnect WebSocket
      disconnect();

      // Reset state
      setIsRecording(false);
      setIsPaused(false);
      setStatus("Arrêté");
      setIsListening(false);

      console.log('Recording stopped');
    });
  }, [disconnect]);

  const togglePause = useCallback(() => {
    if (!isRecording) return;

    setIsPaused(prev => {
      const newPaused = !prev;
      setStatus(newPaused ? "Paused" : "Recording");
      console.log(newPaused ? 'Recording paused' : 'Recording resumed');

      // Note: MediaRecorder continues recording during pause
      // This is intentional - we want the full audio even during pauses

      return newPaused;
    });
  }, [isRecording]);

  // Get the recorded audio blob
  const getRecordedAudio = useCallback(() => {
    const blob = recordedBlobRef.current;
    if (blob) {
      console.log('✅ getRecordedAudio: Returning audio blob of size:', blob.size, 'bytes');
    } else {
      console.log('❌ getRecordedAudio: No audio blob available');
    }
    return blob;
  }, []);

  // Clear the recorded audio (useful for resetting between consultations)
  const clearRecordedAudio = useCallback(() => {
    recordedBlobRef.current = null;
    audioChunksRef.current = [];
    console.log('Recorded audio cleared');
  }, []);

  return {
    micGranted,
    permission,
    isRecording,
    isPaused,
    status,
    isListening,
    hasSpoken,
    requestMic,
    startRecording,
    stopRecording,
    togglePause,
    isConnected,
    drawWaveform: (canvas) => drawWaveform(canvas, analyserRef.current),
    getRecordedAudio,
    clearRecordedAudio
  };
};